import { useEffect, useRef } from 'react';

import { useAtomValue } from 'jotai';
import { includes } from 'lodash';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';
import { PromiseService } from '@/utils/services/PromiseService';

import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';
import { useDMConversationMessages } from '@/utils/hooks/swr/conversations/useDMConversationMessages';
import { useSWRSocket, WEBSOCKET_EVENTS } from '@/utils/hooks/swr/useSWRSocket';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import {
    DMConversation,
    DMConversationRefreshEvent,
    DMInboundMessageEvent
} from '@/components/pages/conversations/SocialInbox/types';

import { DM_STATUS } from '@/components/pages/conversations/SocialInbox/const';

import { openedMessageAtom } from '@/components/pages/conversations/SocialInbox/atoms';

export const useDirectMessageWebSocket = () => {
    const openedConversation = useAtomValue(openedMessageAtom) as DMConversation;

    const { activeSocialProfile } = useActiveSocialProfile();
    const { mutateMessages: mutateConversations, refreshMessages: refreshConversations } =
        useSocialInboxDataSource();
    const { data: conversationMessages, mutate: mutateConversationMessages } =
        useDMConversationMessages({
            activeSPID: activeSocialProfile?.id,
            conversationId: openedConversation?.meta_conversation_id,
            shouldFetch: !!openedConversation
        });
    const { data: event } = useSWRSocket<DMInboundMessageEvent | DMConversationRefreshEvent>();

    const processedMessageIds = useRef<string[]>([]);

    const handleNewMessageEvent = (event: DMInboundMessageEvent) => {
        const newMessageData = event?.data?.message_data;
        const isPartOfOpenedConversation =
            openedConversation?.meta_conversation_id === newMessageData?.meta_conversation_id;
        const isNotProcessed = !includes(
            processedMessageIds.current,
            newMessageData?.meta_message_id
        );

        if (newMessageData && isNotProcessed) {
            processedMessageIds.current.push(newMessageData.meta_message_id);

            // Update the conversations list without revalidating
            mutateConversations(
                (conversations: DMConversation[]) =>
                    DirectMessageService.hydrateConversations(
                        conversations,
                        newMessageData,
                        isPartOfOpenedConversation
                    ),
                { revalidate: false }
            );

            // If the message belongs to the opened conversation, update the messages list
            if (isPartOfOpenedConversation) {
                // Update messages without revalidating
                mutateConversationMessages(
                    (prev) => DirectMessageService.hydrateMessages(prev, newMessageData),
                    { revalidate: false }
                );
            }
        }
    };

    const handleConversationRefreshEvent = async (event: DMConversationRefreshEvent) => {
        const conversationId = event?.data?.meta_conversation_id;
        const key = `conversation-refresh-${conversationId || 'all'}`;

        return PromiseService.deduplicate(key, async () => {
            // When no conversationId is provided, we trigger a refresh of the conversations list
            // via the data source which will revalidate (and may call the API)
            if (!conversationId) {
                refreshConversations();
                return;
            }

            // If the refresh is for the currently opened conversation
            if (conversationId === openedConversation?.meta_conversation_id) {
                const pendingMessages = conversationMessages?.filter(
                    (message) => message.status === DM_STATUS.SENDING
                );

                // Only fetch new data if there are pending messages that need confirmation
                // Otherwise, we already have the data from the inbound message event
                if (!pendingMessages?.length) {
                    // No pending messages - skip revalidation
                    // The new message was already added via INSTAGRAM_INBOUND_MESSAGE event
                    return;
                }

                // Only revalidate if we have pending messages that need status updates
                const refreshedData = await mutateConversationMessages();

                if (pendingMessages?.length > 0 && refreshedData?.length) {
                    const allRefreshedMessages = refreshedData.flatMap((page) => page.data);

                    /**
                     * Re-add pending messages that are not yet in the refreshed data
                     * This handles race conditions where the message is still being processed
                     */
                    const missingPendingMessages = pendingMessages.filter(
                        (pendingMsg) =>
                            !allRefreshedMessages.some(
                                (msg) => msg.meta_message_id === pendingMsg.id
                            )
                    );

                    if (missingPendingMessages.length > 0) {
                        const updatedData = [...refreshedData];
                        const lastPageIndex = updatedData.length - 1;

                        updatedData[lastPageIndex] = {
                            ...updatedData[lastPageIndex],
                            data: [...updatedData[lastPageIndex].data, ...missingPendingMessages]
                        };

                        mutateConversationMessages(updatedData, { revalidate: false });
                    }
                }
            }
        });
    };

    useEffect(() => {
        const {
            INSTAGRAM_INBOUND_MESSAGE,
            FACEBOOK_INBOUND_MESSAGE,
            INSTAGRAM_CONVERSATION_REFRESH,
            FACEBOOK_CONVERSATION_REFRESH
        } = WEBSOCKET_EVENTS;

        if (includes([INSTAGRAM_INBOUND_MESSAGE, FACEBOOK_INBOUND_MESSAGE], event?.event)) {
            handleNewMessageEvent(event as DMInboundMessageEvent);
        } else if (
            includes([INSTAGRAM_CONVERSATION_REFRESH, FACEBOOK_CONVERSATION_REFRESH], event?.event)
        ) {
            handleConversationRefreshEvent(event as DMConversationRefreshEvent);
        }
    }, [event]);
};
